import { useRef, useState, type ReactNode } from 'react';
import {
  ArrowDown, ArrowUp, Bold, Code2, Copy, Heading, Image as ImageIcon, Italic, Link2, Loader2, Minus,
  MoveVertical, RectangleHorizontal, Trash2, Type, Upload, UserRound,
} from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import { bloqueNuevo, idBloque, type Bloque, type TipoBloque } from '@/lib/comunicaciones';

/**
 * Editor de bloques de un correo: se apilan titulares, textos, imágenes,
 * botones, separadores y espacios. El aspecto final no se pinta aquí: lo pinta
 * la vista previa con el HTML que genera el servidor, que es el que se envía.
 *
 * Los textos usan un formato mínimo que el servidor entiende: **negrita**,
 * *cursiva* y [texto](https://…), con línea en blanco entre párrafos y «- »
 * para las listas. La barra de cada texto lo escribe por ti.
 *
 * El bloque de HTML es para pegar un diseño hecho fuera (o un trozo a medida):
 * va entre el logo y el pie legal, y el servidor lo limpia al pintarlo.
 */

const TIPOS: { tipo: TipoBloque; nombre: string; icono: React.ElementType }[] = [
  { tipo: 'titular', nombre: 'Titular', icono: Heading },
  { tipo: 'texto', nombre: 'Texto', icono: Type },
  { tipo: 'imagen', nombre: 'Imagen', icono: ImageIcon },
  { tipo: 'boton', nombre: 'Botón', icono: RectangleHorizontal },
  { tipo: 'separador', nombre: 'Separador', icono: Minus },
  { tipo: 'espacio', nombre: 'Espacio', icono: MoveVertical },
  { tipo: 'html', nombre: 'HTML', icono: Code2 },
];
const NOMBRE = Object.fromEntries(TIPOS.map(t => [t.tipo, t])) as Record<TipoBloque, (typeof TIPOS)[number]>;

export default function EditorBloques({ bloques, onChange }: { bloques: Bloque[]; onChange: (b: Bloque[]) => void }) {
  const cambiar = (i: number, b: Bloque) => onChange(bloques.map((x, j) => (j === i ? b : x)));
  const mover = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= bloques.length) return;
    const n = [...bloques];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  const duplicar = (i: number) => onChange([...bloques.slice(0, i + 1), { ...bloques[i], id: idBloque() }, ...bloques.slice(i + 1)]);
  const quitar = (i: number) => onChange(bloques.filter((_, j) => j !== i));
  const anadir = (tipo: TipoBloque) => onChange([...bloques, bloqueNuevo(tipo)]);

  return (
    <div className="space-y-3">
      {bloques.length === 0 && (
        <div className="text-sm text-sereno-dark/40 text-center border border-dashed border-gray-200 rounded-xl py-8">
          El correo está vacío. Añade un bloque para empezar.
        </div>
      )}
      {bloques.map((b, i) => {
        const { nombre, icono: Icono } = NOMBRE[b.tipo];
        return (
          <div key={b.id} className="bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
              <Icono size={15} className="text-sereno-dark/50" />
              <span className="text-xs font-semibold uppercase tracking-wide text-sereno-dark/50 flex-1">{nombre}</span>
              <BotonIcono titulo="Subir" onClick={() => mover(i, -1)} desactivado={i === 0}><ArrowUp size={15} /></BotonIcono>
              <BotonIcono titulo="Bajar" onClick={() => mover(i, 1)} desactivado={i === bloques.length - 1}><ArrowDown size={15} /></BotonIcono>
              <BotonIcono titulo="Duplicar" onClick={() => duplicar(i)}><Copy size={15} /></BotonIcono>
              <BotonIcono titulo="Quitar" onClick={() => quitar(i)} peligro><Trash2 size={15} /></BotonIcono>
            </div>
            {b.tipo !== 'separador' && <div className="p-3"><Campos b={b} onChange={x => cambiar(i, x)} /></div>}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-xs text-sereno-dark/40 mr-1">Añadir:</span>
        {TIPOS.map(({ tipo, nombre, icono: Icono }) => (
          <button
            key={tipo}
            type="button"
            onClick={() => anadir(tipo)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-slate-300 hover:bg-slate-50/70 text-sereno-dark/70"
          >
            <Icono size={14} /> {nombre}
          </button>
        ))}
      </div>
    </div>
  );
}

function BotonIcono({ titulo, onClick, desactivado, peligro, children }: {
  titulo: string; onClick: () => void; desactivado?: boolean; peligro?: boolean; children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      disabled={desactivado}
      className={classNames(
        'p-1 rounded-md disabled:opacity-25',
        peligro ? 'text-sereno-dark/40 hover:text-red-600 hover:bg-red-50' : 'text-sereno-dark/40 hover:text-sereno-dark hover:bg-slate-100',
      )}
    >
      {children}
    </button>
  );
}

function Opciones<T extends string | number>({ valor, opciones, onChange }: {
  valor: T; opciones: { valor: T; texto: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
      {opciones.map(o => (
        <button
          key={String(o.valor)}
          type="button"
          onClick={() => onChange(o.valor)}
          className={classNames('px-2.5 py-1', o.valor === valor ? 'bg-sereno-dark text-white' : 'text-sereno-dark/60 hover:bg-slate-50/70')}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

function Campos({ b, onChange }: { b: Bloque; onChange: (b: Bloque) => void }) {
  switch (b.tipo) {
    case 'titular':
      return (
        <div className="space-y-2">
          <input className="input text-lg font-semibold" placeholder="Titular" value={b.texto} onChange={e => onChange({ ...b, texto: e.target.value })} />
          <Opciones valor={b.tamano} onChange={tamano => onChange({ ...b, tamano })} opciones={[{ valor: 'grande', texto: 'Grande' }, { valor: 'mediano', texto: 'Mediano' }]} />
        </div>
      );
    case 'texto':
      return (
        <div className="space-y-2">
          <TextoConFormato valor={b.texto} onChange={texto => onChange({ ...b, texto })} />
          <Opciones valor={b.alineacion} onChange={alineacion => onChange({ ...b, alineacion })} opciones={[{ valor: 'izquierda', texto: 'Izquierda' }, { valor: 'centro', texto: 'Centrado' }]} />
        </div>
      );
    case 'imagen':
      return <CamposImagen b={b} onChange={onChange} />;
    case 'boton':
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="input" placeholder="Texto del botón" value={b.texto} onChange={e => onChange({ ...b, texto: e.target.value })} />
            <input className="input" placeholder="https://…" value={b.url} onChange={e => onChange({ ...b, url: e.target.value })} />
          </div>
          <Opciones valor={b.alineacion} onChange={alineacion => onChange({ ...b, alineacion })} opciones={[{ valor: 'izquierda', texto: 'Izquierda' }, { valor: 'centro', texto: 'Centrado' }]} />
        </div>
      );
    case 'espacio':
      return <Opciones valor={b.alto} onChange={alto => onChange({ ...b, alto })} opciones={[{ valor: 16, texto: 'Pequeño' }, { valor: 32, texto: 'Mediano' }, { valor: 48, texto: 'Grande' }]} />;
    case 'separador':
      return null;
    case 'html':
      return (
        <div className="space-y-1.5">
          <textarea
            className="input font-mono text-xs leading-relaxed resize-y min-h-[220px]"
            spellCheck={false}
            value={b.html}
            placeholder={'<p>Hola {{nombre}},</p>\n<p>Pega aquí el HTML…</p>'}
            onChange={e => onChange({ ...b, html: e.target.value })}
          />
          <p className="text-[11px] text-sereno-dark/40 leading-snug">
            Va dentro del correo, entre el logo y el pie legal (con la baja), que se ponen solos. Vale un trozo o un correo entero
            hecho en otra herramienta. Funcionan {'{{nombre}}'} y {'{{email}}'}; los enlaces https:// cuentan en el informe de clics.
            Se quita lo que no funciona en un correo: scripts, marcos y formularios.
          </p>
        </div>
      );
  }
}

/** Textarea con una barra que escribe el formato en el sitio del cursor. */
function TextoConFormato({ valor, onChange }: { valor: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const aplicar = (transformar: (seleccion: string) => { texto: string; cursor: number }) => {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: i, selectionEnd: f } = ta;
    const { texto, cursor } = transformar(valor.slice(i, f));
    onChange(valor.slice(0, i) + texto + valor.slice(f));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(i + cursor, i + cursor);
    });
  };
  const envolver = (marca: string, defecto: string) =>
    aplicar(sel => {
      const t = sel || defecto;
      return { texto: `${marca}${t}${marca}`, cursor: marca.length * 2 + t.length };
    });
  const enlace = () => {
    const url = window.prompt('Dirección del enlace (https://…, mailto:… o tel:…)', 'https://');
    if (!url || url === 'https://') return;
    aplicar(sel => {
      const t = sel || 'texto del enlace';
      const texto = `[${t}](${url.trim()})`;
      return { texto, cursor: texto.length };
    });
  };
  const nombre = () => aplicar(() => ({ texto: '{{nombre}}', cursor: 10 }));

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-sereno-dark/20">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-100 bg-slate-50">
        <BotonIcono titulo="Negrita" onClick={() => envolver('**', 'negrita')}><Bold size={14} /></BotonIcono>
        <BotonIcono titulo="Cursiva" onClick={() => envolver('*', 'cursiva')}><Italic size={14} /></BotonIcono>
        <BotonIcono titulo="Enlace" onClick={enlace}><Link2 size={14} /></BotonIcono>
        <BotonIcono titulo="Insertar el nombre del destinatario" onClick={nombre}><UserRound size={14} /></BotonIcono>
        <span className="ml-auto text-[11px] text-sereno-dark/35 hidden sm:inline">Línea en blanco = párrafo nuevo · «- » = lista</span>
      </div>
      <textarea
        ref={ref}
        className="w-full px-3 py-2 text-sm leading-relaxed bg-white focus:outline-none resize-y min-h-[120px]"
        value={valor}
        onChange={e => onChange(e.target.value)}
        placeholder="Escribe aquí. {{nombre}} pone el nombre de cada destinatario; {{nombre|viajero}} pone «viajero» si no lo hay."
      />
    </div>
  );
}

function CamposImagen({ b, onChange }: { b: Extract<Bloque, { tipo: 'imagen' }>; onChange: (b: Bloque) => void }) {
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const subir = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    setSubiendo(true);
    try {
      const { url } = await api.subirImagenCampana(f);
      onChange({ ...b, url, alt: b.alt || f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ') });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir');
    } finally {
      setSubiendo(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-3 items-start">
        <div className="w-24 h-16 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
          {b.url ? <img src={b.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-sereno-dark/30" />}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex gap-2">
            <button type="button" onClick={() => input.current?.click()} disabled={subiendo} className="btn-ghost border border-gray-200 text-xs flex items-center gap-1.5 px-3 py-1.5">
              {subiendo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {b.url ? 'Cambiar' : 'Subir imagen'}
            </button>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/gif" className="hidden" onChange={e => subir(e.target.files?.[0])} />
            <input className="input text-xs py-1.5" placeholder="…o pega la dirección https:// de la imagen" value={b.url} onChange={e => onChange({ ...b, url: e.target.value })} />
          </div>
          {error && <div className="text-xs text-red-600">{error}</div>}
          <p className="text-[11px] text-sereno-dark/35">JPG, PNG o GIF de hasta 5 MB. Mejor de 1200 px de ancho y menos de 1 MB: los móviles la bajan con datos.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className="input text-sm" placeholder="Texto alternativo (se ve si bloquean las imágenes)" value={b.alt} onChange={e => onChange({ ...b, alt: e.target.value })} />
        <input className="input text-sm" placeholder="Enlace al pulsarla (opcional)" value={b.enlace} onChange={e => onChange({ ...b, enlace: e.target.value })} />
      </div>
      <Opciones valor={b.ancho} onChange={ancho => onChange({ ...b, ancho })} opciones={[{ valor: 'completo', texto: 'Ancho completo' }, { valor: 'medio', texto: 'Media anchura' }]} />
    </div>
  );
}
